/**
 * AdvisoryPane — the advisory findings REPORT, in the details/inspector area.
 *
 * Advisory-severity policy findings are a report artifact, not a diagnostic
 * (design §4): the diagnostic stream drops them, so they surface HERE instead —
 * a collapsible section beneath the selection inspector listing every advisory
 * the workspace's policies raise. Self-sources from `useAdvisoryFindings`; renders
 * nothing when there are none (the common case), so it costs no space until a
 * policy actually advises something.
 */
import type { ReactNode } from "react";
import { Badge } from "@archlang/ui";
import { useAdvisoryFindings } from "../../views/useAdvisoryFindings.js";
import { useTranslation } from "../../i18n";

export function AdvisoryPane(): ReactNode {
  const { t } = useTranslation("advisory");
  const findings = useAdvisoryFindings();
  if (findings.length === 0) return null;
  return (
    <details
      open
      className="border-t border-slate-800 bg-slate-900/(--tint-dim) text-slate-300"
      aria-label={t("findingsAria")}
    >
      <summary className="flex cursor-pointer select-none items-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-200">
        <Badge count={findings.length} tone="info" />
        {t("heading")}
      </summary>
      <ul className="space-y-1 px-3 pb-3 pt-1">
        {findings.map((f, i) => (
          <li key={`${f.policy}:${f.anchorKey}:${i}`} className="text-xs leading-snug text-slate-400">
            {f.message}
          </li>
        ))}
      </ul>
    </details>
  );
}
